import { useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminTextInput from "../common/AdminTextInput";
import AdminButton from "../common/AdminButton";
import { findNavigationItem } from "./adminNavigation";

interface AdminShellTopBarProps {
  sidebarCollapsed: boolean;
  onOpenMobile: () => void;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  hasIssue?: boolean;
  issueLabel?: string;
}

const AdminShellTopBar = ({ sidebarCollapsed, onOpenMobile, title, subtitle, actions, hasIssue = false, issueLabel = "확인 필요" }: AdminShellTopBarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const activeNavigation = useMemo(() => findNavigationItem(location.pathname), [location.pathname]);

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = searchQuery.trim();
    if (!keyword) return;
    navigate("/admin/users", { state: { globalSearchKeyword: keyword } });
  };

  return (
    <header
      className={`fixed right-0 top-0 z-30 border-b px-4 py-3 transition-[left] duration-300 lg:px-6 ${sidebarCollapsed ? "left-0 lg:left-[4.5rem]" : "left-0 lg:left-[16.5rem]"}`}
      style={{
        background: "rgba(243, 246, 251, 0.97)",
        backdropFilter: "blur(0.625rem)",
        borderColor: "#E2E8F0",
      }}
    >
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <AdminButton variant="plain" size="auto"
            type="button"
            aria-label="관리자 메뉴 열기"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 lg:hidden"
            onClick={onOpenMobile}
          >
            <i className="ri-menu-line text-lg" />
          </AdminButton>
          <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm ring-1 ring-slate-200 md:flex">
            <i className={`${activeNavigation?.item.icon ?? "ri-dashboard-3-line"} text-lg`} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {activeNavigation && <span className="text-xs font-semibold text-blue-600">{activeNavigation.group.section}</span>}
              <h1 className="truncate text-base font-bold text-slate-900">{title}</h1>
            </div>
            <p className="truncate text-xs text-slate-500">{subtitle ?? activeNavigation?.item.description ?? "관리자 최종본 운영 화면"}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
          <form onSubmit={handleSearchSubmit} className="flex min-w-56 flex-1 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm xl:w-72 xl:flex-none">
            <i className="ri-search-line text-sm text-slate-400" />
            <AdminTextInput
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Senior, 보호자, 기기 검색"
              variant="bare"
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
            />
          </form>

          {actions}

          <div className={`hidden items-center gap-1.5 rounded-full border px-2.5 py-1 md:flex ${hasIssue ? "border-red-200 bg-red-50" : "border-teal-200 bg-teal-50"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${hasIssue ? "bg-red-500" : "bg-teal-500"} animate-pulse`} />
            <span className={`text-xs font-semibold ${hasIssue ? "text-red-700" : "text-teal-700"}`}>{hasIssue ? issueLabel : "시스템 정상"}</span>
          </div>

          <div className="flex items-center gap-2 rounded-full bg-white py-1 pl-1 pr-3 shadow-sm ring-1 ring-slate-200">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800">
              <i className="ri-user-line text-xs text-white" />
            </div>
            <span className="hidden text-sm font-semibold text-slate-700 sm:inline">운영 관리자</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminShellTopBar;
